import { Injectable } from '@angular/core';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import { ComponentType } from '@angular/cdk/portal';
import { MatDialog } from '@angular/material/dialog';
import { CognitoUtil } from './cognito.service';
import { from, Observable } from 'rxjs';
import { StateManagementService } from '../services/state-management.service';
import { Page, role } from './user-roles';
import { Brand } from '../models/types';
import variableMapping, { kwiksetunite, weiser } from 'src/themes/themes';
import { Theme } from 'src/themes/theme-interfaces';
import { BRANDS } from '../models/screen-size.enum';
import { PhoneNumberUtil } from 'google-libphonenumber';
import { PhoneNumberFormatPipe } from 'src/app/libs/ui/pipes/phone-number-format/phone-number-format.pipe';
import { config } from 'src/config/config';

@Injectable({
  providedIn: 'root',
})
export class HelperService {
  private phoneUtil: any;

  constructor(
    private matBottomSheet: MatBottomSheet,
    private matDialog: MatDialog,
    private cognitoUtil: CognitoUtil,
    private stateManagementService: StateManagementService,
    private phoneNumberFormatPipe: PhoneNumberFormatPipe
  ) {
    this.phoneUtil = PhoneNumberUtil.getInstance();
  }

  /**
   *
   * @param component Pass any component refrence
   * @param arg any type of arg object and array
   * @param panelStyleClass any specific styling class
   * @returns
   */
  openBottomSheet(
    component: ComponentType<any>,
    arg?: any,
    panelStyleClass?: any
  ): any {
    return this.matBottomSheet.open(component, {
      data: arg,
      panelClass: panelStyleClass ?? null,
    });
  }

  /**
   * Close current bottom sheet
   */
  closeCurrentBottomSheet() {
    this.matBottomSheet.dismiss();
  }

  /**
   *
   * @param component Pass any component refrence
   * @param arg any type of arg object and array
   * @param panelStyleClass any specific styling class
   * @returns
   */
  openModalDialog(
    component: ComponentType<any>,
    arg?: any,
    panelStyleClass?: any,
    disableClose?: boolean
  ): any {
    return this.matDialog.open(component, {
      data: arg,
      panelClass: panelStyleClass ?? null,
      disableClose: disableClose ?? true,
    });
  }

  /**
   * Close all modal popup
   */
  closeAllMatDialog() {
    this.matDialog.closeAll();
  }

  /**
   *
   * @returns Access token in promise | return null reject if user not login
   */
  getToken(): Observable<any> {
    let currentUser = this.cognitoUtil.getCurrentUser();

    if (!currentUser) {
      // redirect to login page
      return from(
        new Promise((resolve, reject) => {
          resolve(null);
        })
      );
    } else {
      return from(
        new Promise((resolve, reject) => {
          currentUser?.getSession((err: any, session: any) => {
            if (err) {
              reject(null);
            } else if (!session.isValid()) {
              reject(null);
            } else {
              resolve(session.getAccessToken().getJwtToken());
            }
          });
        })
      );
    }
  }

  /**
   * Get current user roll
   */
  get getUserRole(): role {
    const userObject = this.stateManagementService.getUserObject();
    return userObject?.role;
  }

  /**
   * Get current user accessibility matrix
   */
  get getUserAccessibilityMatrix(): Page[] {
    const userObject = this.stateManagementService.getUserObject();
    return userObject?.matrix;
  }

  /**
   * @description This method set theme dynamically by modifying CSS variables and run-time
   * @param { Brand } theme - brand name to adjust theme accordingly
   */

  setTheme(theme?: Brand): void {
    let selectedTheme: Theme;
    switch (theme) {
      case BRANDS.KWIKSETUNITE:
        selectedTheme = kwiksetunite;
        break;
      case BRANDS.WEISER:
        selectedTheme = weiser;
        break;
      default:
        selectedTheme = kwiksetunite;
        break;
    }
    //default user brand is kwiksetunite
    for (let key in variableMapping) {
      //parsed key as keyof type Theme
      const parsedKey = key as keyof Theme;
      //setting all the variables for dynamic themes
      document.documentElement.style.setProperty(
        variableMapping[parsedKey],
        selectedTheme[parsedKey]
      );
    }
  }

  /**
   *
   * @returns validate Phone Format in observable | return  region, phone number, and code in case any error occur format through error
   */
  validatePhoneFormat(phoneNumber: string | undefined): Observable<any> {
    return new Observable((observer) => {
      if (!phoneNumber) {
        observer.error(null);
      } else {
        const phone = this.phoneUtil.parseAndKeepRawInput(phoneNumber);
        const dialCode = '+' + phone.getCountryCode();
        const selectedCountryCode =
          this.phoneUtil.getRegionCodeForNumber(phone);
        const inputNumber = phoneNumber?.substring(dialCode.length);
        observer.next({
          region: selectedCountryCode,
          code: dialCode,
          phoneNumber: this.phoneNumberFormatPipe.transform(
            inputNumber,
            selectedCountryCode
          ),
        });
      }

      observer.complete();
    });
  }

  /**
   *
   * @return  phoneNumberLength function check the selected region and return the formated length
   */

  phoneNumberLength(region: any) {
    if (region === config.COUNTRIES.US.COUNTRY_CODE) {
      return config.COUNTRIES.US.FORMATTED_LENGTH;
    } else {
      return 0;
    }
  }
}
